import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { ShieldCheck, ShieldAlert, MailCheck, MailWarning, RefreshCcw, Search } from "lucide-react";

/**
 * AdminCompliance: read-only overview of legal-acceptance & email
 * verification status across all users and businesses.
 *
 * Backend endpoints used (all gated to super-admin):
 *   GET /api/admin/compliance/users?missing_legal=1&unverified_email=1
 *   GET /api/admin/compliance/businesses?missing_business_terms=1
 *
 * No mutation is exposed here on purpose: the user's "do not silently fake
 * acceptance" rule means manual override happens elsewhere (and never
 * silently). Re-accept is initiated by the user themselves via the
 * LegalReacceptGate.
 */
export default function AdminCompliance() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "users";

  return (
    <div data-testid="admin-compliance-page">
      <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
        <ShieldCheck className="w-5 h-5" /> Compliance
      </h1>
      <p className="text-sm text-slate-600 mt-1 max-w-3xl">
        Read-only view of legal acceptance and email verification across the
        platform. Use these lists to nudge users or unblock businesses that
        haven&apos;t re-accepted the latest bundle. No silent overrides are
        possible from here: every override is audit-logged elsewhere.
      </p>

      <div className="mt-4 flex flex-wrap gap-2 text-sm">
        <TabBtn active={tab === "users"}      onClick={() => setParams({ tab: "users" })}      testId="compliance-tab-users">
          Users
        </TabBtn>
        <TabBtn active={tab === "businesses"} onClick={() => setParams({ tab: "businesses" })} testId="compliance-tab-businesses">
          Businesses
        </TabBtn>
      </div>

      <div className="mt-4">
        {tab === "users" ? <UsersTable initialUnverified={params.get("unverified") === "1"} />
                          : <BusinessesTable />}
      </div>
    </div>
  );
}

function TabBtn({ active, children, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`px-3 py-1.5 rounded-lg border text-sm transition ${
              active ? "bg-[#1B4332] text-white border-[#1B4332]"
                     : "bg-white border-slate-300 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

function UsersTable({ initialUnverified }) {
  const [missingLegal, setMissingLegal]   = useState(false);
  const [unverified,   setUnverified]     = useState(!!initialUnverified);
  const [q,            setQ]              = useState("");
  const [data,         setData]           = useState({ items: [], count: 0 });
  const [loading,      setLoading]        = useState(false);

  const load = useCallback(() => {
    const search = new URLSearchParams();
    if (missingLegal) search.set("missing_legal", "true");
    if (unverified)   search.set("unverified_email", "true");
    if (q.trim())     search.set("q", q.trim());
    search.set("limit", "200");
    setLoading(true);
    api.get(`/admin/compliance/users?${search}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [missingLegal, unverified, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input data-testid="compliance-users-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Email or name…"
                 className="pl-7 pr-3 py-1.5 text-sm rounded-lg border border-slate-300" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="compliance-users-missing-legal" checked={missingLegal}
                 onChange={(e) => setMissingLegal(e.target.checked)} />
          Missing latest legal acceptance
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="compliance-users-unverified" checked={unverified}
                 onChange={(e) => setUnverified(e.target.checked)} />
          Unverified email
        </label>
        <button onClick={load}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-xs text-slate-500 ml-auto">
          {loading ? "Loading…" : `${data.count} users`} · current bundle:{" "}
          <span className="font-mono">{data.current_legal_bundle_version || "-"}</span>
        </span>
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Name</th>
              <th className="text-left p-2">Role</th>
              <th className="text-left p-2">Email verified</th>
              <th className="text-left p-2">Legal bundle accepted</th>
              <th className="text-left p-2">Terms accepted at</th>
              <th className="text-left p-2">IP</th>
              <th className="text-left p-2">Created</th>
            </tr>
          </thead>
          <tbody data-testid="compliance-users-tbody">
            {data.items.map((u) => (
              <tr key={u.id} data-testid={`compliance-user-row-${u.id}`}
                  className="border-t border-slate-100">
                <td className="p-2 font-medium">{u.email}</td>
                <td className="p-2">{u.name}</td>
                <td className="p-2 text-xs">
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">
                    {u.account_type || u.role}
                  </span>
                </td>
                <td className="p-2">
                  {u.email_verified
                    ? <span className="inline-flex items-center gap-1 text-emerald-700"><MailCheck className="w-3 h-3" /> Yes</span>
                    : <span className="inline-flex items-center gap-1 text-amber-700"><MailWarning className="w-3 h-3" /> No</span>}
                </td>
                <td className="p-2 font-mono text-[11px]">
                  {u.accepted_legal_bundle_version || (
                    <span className="text-rose-600">- missing</span>
                  )}
                </td>
                <td className="p-2 text-[11px] text-slate-600">{(u.terms_accepted_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="p-2 text-[11px] text-slate-600">{u.accepted_from_ip || "-"}</td>
                <td className="p-2 text-[11px] text-slate-600">{(u.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {data.items.length === 0 && !loading && (
              <tr><td colSpan={8} className="p-6 text-center text-slate-500">No users match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BusinessesTable() {
  const [missing, setMissing] = useState(false);
  const [data,    setData]    = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    const search = new URLSearchParams();
    if (missing) search.set("missing_business_terms", "true");
    search.set("limit", "200");
    setLoading(true);
    api.get(`/admin/compliance/businesses?${search}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [missing]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="compliance-biz-missing-terms" checked={missing}
                 onChange={(e) => setMissing(e.target.checked)} />
          Missing latest Business Terms acceptance
        </label>
        <button onClick={load}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-xs text-slate-500 ml-auto">
          {loading ? "Loading…" : `${data.count} businesses`} · current bundle:{" "}
          <span className="font-mono">{data.current_legal_bundle_version || "-"}</span>
        </span>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2">Business</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Plan</th>
              <th className="text-left p-2">Business Terms version</th>
              <th className="text-left p-2">Accepted at</th>
              <th className="text-left p-2">Created</th>
            </tr>
          </thead>
          <tbody data-testid="compliance-biz-tbody">
            {data.items.map((c) => (
              <tr key={c.id} data-testid={`compliance-biz-row-${c.id}`}
                  className="border-t border-slate-100">
                <td className="p-2 font-medium">{c.name}</td>
                <td className="p-2 text-slate-600">{c.status}</td>
                <td className="p-2 text-[11px]">
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 uppercase">{c.plan_type || "-"}</span>
                </td>
                <td className="p-2 font-mono text-[11px]">
                  {c.business_terms_version || <span className="text-rose-600">- missing</span>}
                </td>
                <td className="p-2 text-[11px] text-slate-600">{(c.business_terms_accepted_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="p-2 text-[11px] text-slate-600">{(c.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {data.items.length === 0 && !loading && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">No businesses match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
